import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, User, Mail, Phone, Clock, Users, MessageSquare, RefreshCw, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchBookings = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' ? `${API}/bookings` : `${API}/bookings?status=${filter}`;
      const response = await axios.get(url);
      setBookings(response.data);
    } catch (error) {
      console.error('Erro ao buscar reservas:', error);
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Status atualizado para ${newStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const requestReview = async (booking) => {
    if (booking.review_requested_at) {
      const ok = window.confirm('Já foi enviado um pedido de review a este cliente. Enviar novamente?');
      if (!ok) return;
    }
    try {
      const res = await axios.post(`${API}/bookings/${booking.id}/request-review`);
      if (res.data?.already_sent) {
        toast.info('Pedido de review já tinha sido enviado anteriormente');
      } else {
        toast.success(`⭐ Email enviado em ${(booking.language || 'en').toUpperCase()}`);
      }
      fetchBookings();
    } catch (error) {
      toast.error('Erro ao enviar pedido de review');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-500';
      case 'confirmed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Pendente';
      case 'confirmed': return 'Confirmada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const getServiceName = (serviceCode) => {
    const services = {
      'transfer-airport-porto': 'Transfer Aeroporto Porto',
      'transfer-airport-gaia': 'Transfer Aeroporto Gaia',
      'braga': 'Tour Braga',
      'guimaraes': 'Tour Guimarães',
      'santiago': 'Tour Santiago de Compostela',
      'aveiro': 'Tour Aveiro',
      'fatima': 'Tour Fátima',
      'nazare': 'Tour Nazaré',
      'douro': 'Tour Douro Vinhateiro',
      'paiva': 'Tour Passadiços do Paiva',
      'custom': 'Tour Personalizado'
    };
    return services[serviceCode] || serviceCode;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Painel de Reservas</h1>
            <p className="text-gray-600">Gerencie todas as reservas da GO VIP</p>
          </div>
          <a
            href="/admin/marketing"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white px-4 py-2.5 rounded-lg font-medium shadow-sm transition-all"
            data-testid="link-to-marketing"
          >
            <Star className="w-4 h-4" />
            Marketing AI
          </a>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            Todas
          </Button>
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilter('pending')}
            className={filter === 'pending' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            Pendentes
          </Button>
          <Button
            variant={filter === 'confirmed' ? 'default' : 'outline'}
            onClick={() => setFilter('confirmed')}
            className={filter === 'confirmed' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            Confirmadas
          </Button>
          <Button
            variant={filter === 'cancelled' ? 'default' : 'outline'}
            onClick={() => setFilter('cancelled')}
            className={filter === 'cancelled' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            Canceladas
          </Button>
          <Button
            variant="outline"
            onClick={fetchBookings}
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">{bookings.length}</div>
                <div className="text-sm text-gray-600 mt-1">Total de Reservas</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {bookings.filter(b => b.status === 'pending').length}
                </div>
                <div className="text-sm text-gray-600 mt-1">Pendentes</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </div>
                <div className="text-sm text-gray-600 mt-1">Confirmadas</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Carregando reservas...</p>
          </div>
        ) : bookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">Nenhuma reserva encontrada</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{booking.name}</CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        ID: {booking.id.substring(0, 8)}
                      </p>
                    </div>
                    <Badge className={`${getStatusColor(booking.status)} text-white`}>
                      {getStatusText(booking.status)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">{booking.email}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">{booking.phone}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">
                          {booking.passengers} {booking.passengers === '1' ? 'passageiro' : 'passageiros'}
                        </span>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">{booking.date}</span>
                      </div>
                      {booking.time && (
                        <div className="flex items-center space-x-3">
                          <Clock className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-700">{booking.time}</span>
                        </div>
                      )}
                      <div className="flex items-start space-x-3">
                        <User className="w-5 h-5 text-gray-400 mt-0.5" />
                        <span className="text-gray-700 font-medium">
                          {getServiceName(booking.service)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  {booking.message && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-start space-x-3">
                        <MessageSquare className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 mb-1">Mensagem:</p>
                          <p className="text-sm text-gray-600">{booking.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-200">
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateStatus(booking.id, 'confirmed')}
                        >
                          Confirmar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateStatus(booking.id, 'cancelled')}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                    {booking.status === 'confirmed' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(booking.id, 'pending')}
                        >
                          Marcar como Pendente
                        </Button>
                        <Button
                          size="sm"
                          className={booking.review_requested_at
                            ? 'bg-gray-400 hover:bg-gray-500'
                            : 'bg-amber-500 hover:bg-amber-600 text-white'}
                          onClick={() => requestReview(booking)}
                          data-testid={`request-review-${booking.id}`}
                          title={booking.review_requested_at ? 'Já enviado' : 'Enviar pedido de avaliação Google'}
                        >
                          <Star className="w-4 h-4 mr-1" />
                          {booking.review_requested_at ? 'Review enviado ✓' : 'Pedir Review'}
                        </Button>
                      </>
                    )}
                    {booking.status === 'cancelled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(booking.id, 'pending')}
                      >
                        Reativar
                      </Button>
                    )}
                    <a
                      href={`https://wa.me/${booking.phone.replace(/\s+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto"
                    >
                      <Button size="sm" variant="outline" className="bg-green-50 hover:bg-green-100">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
